/** Τύπος μετατροπής τιμής στήλης Excel σε τιμή συστήματος */
export type ImportResolver =
  | 'text'
  | 'number'
  | 'date'
  | 'legacyAppId'
  | 'jobtypeCode'
  | 'wrpErpId';

/** Ορισμός πεδίου εισαγωγής — συμπληρώστε aliases, description, example */
export type ImportFieldDef = {
  /** Εσωτερικό όνομα πεδίου */
  field: string;
  /** Αποδεκτά ονόματα στήλης — το πρώτο είναι το canonical (για template) */
  labels: string[];
  /** Υποχρεωτική στήλη στο Excel */
  required: boolean;
  /** Περιγραφή για τον χρήστη (συμπληρώνεται από διαχειριστή) */
  description: string;
  /** Παράδειγμα τιμής */
  example?: string;
  /** Τρόπος επίλυσης τιμής κατά την εισαγωγή */
  resolver?: ImportResolver;
  /** Σημειώσεις (π.χ. εναλλακτικές στήλες κωδικού) */
  notes?: string;
};

/**
 * Schema εισαγωγής συλλογών από Excel.
 * Τα πεδία code / code_01 / code_02 είναι προαιρετικά ξεχωριστά·
 * απαιτείται όμως είτε η στήλη code είτε το ζεύγος code_01 + code_02.
 */
export const COLLECTION_IMPORT_FIELDS: readonly ImportFieldDef[] = [
  {
    field: 'code',
    labels: ['Κωδικός Εντύπου'],
    required: false,
    description: 'Ο κωδικός του Δελτίου Αποστολής',
    example: 'ΑΔΠ-Δ-12345',
    resolver: 'text',
    notes: 'Εναλλακτικά των στηλών Σειρά + Αριθμός Παραστατικού. Χρησιμοποιείται και για αντιστοίχιση με αίτηση (finDispNote, αγνοώντας κενά).',
  },
  {
    field: 'code_01',
    labels: ['Σειρά Παραστατικού'],
    required: false,
    description: 'Η σειρά (το πρόθεμα) του Δελτίου Αποστολής',
    example: 'ΑΔΠ-Δ',
    resolver: 'text',
    notes: 'Χρησιμοποιείται μαζί με τον Αριθμό Παραστατικού',
  },
  {
    field: 'code_02',
    labels: ['Αριθμ. Παραστατικού'],
    required: false,
    description: 'Ο αριθμός του Δελτίου Αποστολής',
    example: '12345',
    resolver: 'text',
    notes:
      'Μαζί με τη Σειρά σχηματίζει το παραστατικό. Αντιστοιχίζεται με το ΔΑ της αίτησης (χωρίς κενά) και το σημείο συλλογής.',
  },
  {
    field: 'collectionPointId',
    labels: ['Κωδικός Σημείου Συλλογής', 'Υποκ/μα'],
    required: true,
    description: 'Ο κωδικός του Σημείου Συλλογής',
    example: '1025',
    resolver: 'legacyAppId',
    notes: 'Ο "κωδικός erp" απο την καρτέλα του Σημείου Συλλογής. Ο κωδικός είναι μοναδικός.',
  },
  {
    field: 'wastereceptionpointId',
    labels: ['Κωδικός Σημείου Παραλαβής', 'Σημείο Παραλαβής'],
    required: true,
    description: 'Ο ERP κωδικός του Σημείου Παραλαβής',
    example: 'WRP-001',
    resolver: 'wrpErpId',
    notes:
      'Ο "ERP Κωδικός" από την καρτέλα του Σημείου Παραλαβής. Πρέπει να ανήκει στον επιλεγμένο Συλλέκτη.',
  },
  {
    field: 'dateCollected',
    labels: ['Ημερομηνία', 'Ημερομηνία Συλλογής'],
    required: true,
    description: 'Η ημερομηνία συλλογής των αποβλήτων',
    example: '04/02/2026',
    resolver: 'date',
  },
  {
    field: 'jobtypeId',
    labels: ['Κωδικός Εργασίας', 'Κωδικός είδους'],
    required: true,
    description: 'Ο κωδικός της εργασίας',
    notes:
      'Ο κωδικός της εργασίας απο την καρτέλα των Κατηγορίες Εργασίας. Ο κωδικός είναι μοναδικός και συσχετίζεται με ένα Απόβλητο.',
    example: '0311',
    resolver: 'jobtypeCode',
  },
  {
    field: 'qty',
    labels: ['Ποσότητα'],
    required: true,
    description: 'Ποσότητα συσκευασιών σε τεμάχια',
    example: '1',
    resolver: 'number',
  },
  {
    field: 'netWeight',
    labels: ['Βάρος (kg)', 'Δευτ.Ποσότητα'],
    required: true,
    description: 'Βάρος αποβλήτων σε κιλά',
    example: '36.2',
    resolver: 'number',
  },
] as const;

/** Canonical τίτλος στήλης (πρώτο label) */
export function canonicalLabel(def: ImportFieldDef): string {
  return def.labels[0];
}

/** Πεδία που εμφανίζονται στο template δεδομένων (χωρίς code_01/02 όταν υπάρχει code) */
export function templateDataFields(): ImportFieldDef[] {
  return COLLECTION_IMPORT_FIELDS.filter((f) => f.field !== 'code_01' && f.field !== 'code_02');
}
